import type { EventSummary } from '../data/types'
import { deriveStatus, entryLimitAndCount } from '../data/registrationStatus'
import { formatDateColumn } from '../data/format'
import { NoFlyerTile } from './NoFlyerTile'
import { StatusBadge, CtaButton } from './StatusBadge'
import { entryMeta } from './EventListCard'
import styles from './EventCard.module.css'

const RACE_TYPE_LABEL: Record<string, string> = { offroad: 'Off-road', onroad: 'On-road', oval: 'Oval' }

/** Desktop list row, Desktop.dc.html's scannable grid: flyer thumb | date column | title/track/
 *  tags | status + progress | CTA. */
export function EventRow({ event, canSignUp }: { event: EventSummary; canSignUp: boolean }) {
  const href = `/events/displayEvent.cfm?ID=${event.id}`
  const status = deriveStatus(event)
  const { dow, day, span } = formatDateColumn(event.startDT, event.endDT)
  const { limit, count } = entryLimitAndCount(event)
  const fillPct = limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0

  const tags = [RACE_TYPE_LABEL[event.raceType], `${event.classCount} class${event.classCount === 1 ? '' : 'es'}`].filter(Boolean)

  return (
    <article className={styles.row}>
      <a href={href} aria-label={`View ${event.name}`} className={styles.rowFlyerLink}>
        {event.flyerUrl ? (
          <img src={event.flyerUrl} alt="" className={styles.rowFlyerImg} />
        ) : (
          <NoFlyerTile id={event.id} name={event.name} raceType={event.raceType} />
        )}
      </a>
      <div className={styles.rowDateCol}>
        <div className={styles.rowDow}>{dow}</div>
        <div className={styles.rowDay}>{day}</div>
        <div className={styles.rowSpan}>{span}</div>
      </div>
      <div className={styles.rowInfo}>
        <a href={href} className={styles.rowTitle}>
          {event.name}
        </a>
        <div className={styles.rowTrack}>
          {event.trackName} <span className={styles.rowTrackMuted}>· {event.city}, {event.state}</span>
        </div>
        {tags.length ? (
          <div className={styles.rowTags}>
            {tags.map((t) => (
              <span key={t} className={styles.rowTag}>
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className={styles.rowStatusCol}>
        <StatusBadge status={status} />
        {canSignUp && limit > 0 ? (
          <div className={styles.rowProgress}>
            <div className={styles.rowProgressFill} style={{ width: `${fillPct}%`, background: status.dotColor }} />
          </div>
        ) : null}
        {canSignUp ? <span className={styles.rowMeta}>{entryMeta(event, canSignUp)}</span> : null}
      </div>
      <CtaButton status={status} href={href} canSignUp={canSignUp} />
    </article>
  )
}
